import { Product, SkeletonProduct } from "../types/types";
import { useState, useEffect } from "react";
import { FeatureFlags } from "@packages/feature-flags";
import { isOutOfStock, isRetiredProduct } from "./product";
import { productCardEvents } from "@~/contexts/ProductCardEventContext/productCardEvents";

export const useVisibleProducts = (
  products: (Product | SkeletonProduct)[],
  flags: FeatureFlags,
  ref: React.RefObject<HTMLElement | null>,
) => {
  const [visibleProducts, setVisibleProducts] = useState(
    filterVisibleProducts(
      products,
      !!flags.hideOutOfStocks?.value,
      !!flags.hideRetiredProducts?.value,
    ),
  );

  useEffect(
    function updateVisibleProductsFromProps() {
      setVisibleProducts(
        filterVisibleProducts(
          products,
          !!flags.hideOutOfStocks?.value,
          !!flags.hideRetiredProducts?.value,
        ),
      );
    },
    [products, flags.hideOutOfStocks, flags.hideRetiredProducts],
  );

  useEffect(function subscribeToRemoveProductEvents() {
    const handleRemoveProduct = (event: Event) => {
      const removeEvent = event as CustomEvent<{
        pimProductId: string;
        variantId?: string;
      }>;
      const removedProductId = removeEvent.detail?.pimProductId;
      const removedProductVariantId = removeEvent.detail?.variantId;

      if (!removedProductId) {
        return;
      }
      setVisibleProducts((currentProducts) =>
        currentProducts.filter((product) =>
          product.__typename !== "SkeletonProduct" &&
          product.initialVariantId &&
          removedProductVariantId
            ? product.initialVariantId !== removedProductVariantId
            : product.aliases?.pimProductId !== removedProductId,
        ),
      );
    };

    const root = ref.current?.getRootNode();

    root?.addEventListener(
      productCardEvents.removeProduct,
      handleRemoveProduct,
    );

    return function unsubscribeFromRemoveProductEvents() {
      root?.removeEventListener(
        productCardEvents.removeProduct,
        handleRemoveProduct,
      );
    };
  }, []);

  return visibleProducts;
};

const filterVisibleProducts = (
  products: (Product | SkeletonProduct)[],
  hideOutOfStocks: boolean,
  hideRetiredProducts: boolean,
): (Product | SkeletonProduct)[] => {
  return products.filter((product) => {
    if (product.__typename === "SkeletonProduct") {
      return true;
    }
    if (
      (hideOutOfStocks && isOutOfStock(product)) ||
      (hideRetiredProducts && isRetiredProduct(product))
    ) {
      return false;
    }

    return true;
  });
};
